import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Room, Track } from 'livekit-client';

// ─── constants ──────────────────────────────────────────────────────────────

const ENERGIES = [
  { id: 'hot',   emoji: '🔥', label: 'Hot Take' },
  { id: 'real',  emoji: '🧠', label: 'Real Talk' },
  { id: 'con',   emoji: '💌', label: 'Confession' },
  { id: 'q',     emoji: '❓', label: 'Question' },
  { id: 'w',     emoji: '🏆', label: 'The W' },
  { id: 'event', emoji: '📍', label: 'Event' },
  { id: 'plug',  emoji: '🛍', label: 'The Plug' },
  { id: 'psa',   emoji: '📢', label: 'PSA' },
];

const BG_COLORS = [
  null, '#C0392B', '#2C3E8C', '#D35400', '#2C2C2C',
  '#7C4FD4', '#E67E22', '#1A3A5C', '#1E6B3C', '#C0185D',
];

const MODE_LABELS = {
  drop:     'THE DROP',
  moment:   'THE MOMENT',
  reel:     'THE REEL',
  rotation: 'THE ROTATION',
  live:     'THE STOOP LIVE',
  byline:   'THE BYLINE',
};

// ─── DROP MODE — text-first compose ─────────────────────────────────────────

function DropMode({ onGallery, selectedMedia, previewUrl, user }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [energy, setEnergy] = useState(null);
  const [destination, setDestination] = useState('block');
  const [bgColor, setBgColor] = useState(null);
  const [posting, setPosting] = useState(false);

  const canPost = text.trim().length > 0 || energy !== null;

  const handlePost = async () => {
    if (!canPost) return;
    setPosting(true);
    try {
      await api.post('/posts', {
        content: text.trim(),
        post_type: 'original',
        visibility: destination,
        energy,
        background: bgColor ? { type: 'solid', value: bgColor } : null,
        media_url: previewUrl || null,
        media_type: selectedMedia
          ? selectedMedia.type.startsWith('video/') ? 'video' : 'image'
          : null,
      }, { withCredentials: true });
      localStorage.setItem('blvx-lastUsedFormat', 'drop');
      toast.success('Dropped!');
      navigate('/home');
    } catch (err) {
      toast.error('Failed to post. Try again.');
      console.error(err);
    } finally {
      setPosting(false);
    }
  };

  // Convert hex bgColor to rgba tint for compose area
  const composeTint = bgColor
    ? bgColor + '1F' // ~12% opacity tint
    : 'transparent';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#050608' }}>

      {/* ENERGY PILLS */}
      <div style={{ padding: '12px 16px 8px', flexShrink: 0 }}>
        <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>
          What's the energy?
        </div>
        <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {ENERGIES.map(e => (
            <button
              key={e.id}
              onClick={() => setEnergy(energy === e.id ? null : e.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '7px 12px', borderRadius: '20px', cursor: 'pointer',
                background: energy === e.id ? 'rgba(240,123,29,.15)' : 'rgba(255,255,255,.06)',
                border: energy === e.id ? '1px solid rgba(240,123,29,.4)' : '1px solid rgba(255,255,255,.08)',
                color: energy === e.id ? '#F07B1D' : '#A0A6B8',
                fontFamily: "'Instrument Sans', sans-serif",
                fontSize: '13px', whiteSpace: 'nowrap', flexShrink: 0,
                transition: 'all .14s',
              }}
            >
              <span>{e.emoji}</span>
              <span style={{ fontSize: '12px' }}>{e.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* BG COLOR SWATCHES */}
      <div style={{ padding: '4px 16px 12px', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {BG_COLORS.map((c, i) => (
            <button
              key={i}
              onClick={() => setBgColor(c)}
              style={{
                width: '28px', height: '28px', borderRadius: '50%',
                background: c || 'transparent',
                border: bgColor === c ? '2px solid #F07B1D' : c === null ? '1.5px solid rgba(255,255,255,.2)' : '2px solid transparent',
                cursor: 'pointer', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#72788A', fontSize: '14px',
              }}
            >
              {c === null && '✕'}
            </button>
          ))}
        </div>
      </div>

      {/* TEXT INPUT — tinted compose area */}
      <div style={{ flex: 1, margin: '0 12px', padding: '12px', borderRadius: '8px', background: composeTint, transition: 'background .2s', display: 'flex', gap: '10px', overflow: 'hidden' }}>
        <div style={{
          width: '38px', height: '38px', borderRadius: '50%',
          background: user?.picture ? undefined : 'linear-gradient(135deg, #F07B1D, #C9950C)',
          backgroundImage: user?.picture ? `url(${user.picture})` : undefined,
          backgroundSize: 'cover', backgroundPosition: 'center',
          flexShrink: 0, marginTop: '2px',
        }} />
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="What's happening on the yard?"
          maxLength={500}
          autoFocus
          style={{
            flex: 1, background: 'none', border: 'none', outline: 'none',
            color: '#EAE0CC', resize: 'none',
            fontFamily: "'Instrument Sans', sans-serif",
            fontSize: '17px', lineHeight: 1.5, padding: '2px 0',
          }}
        />
      </div>

      {/* MEDIA PREVIEW */}
      {previewUrl && (
        <div style={{ padding: '0 16px 8px', flexShrink: 0 }}>
          <img src={previewUrl} alt="" style={{ maxHeight: '200px', borderRadius: '12px', objectFit: 'cover', maxWidth: '100%' }} />
        </div>
      )}

      {/* MEDIA ROW — Gallery | GIF | Music only */}
      <div style={{ padding: '10px 16px', display: 'flex', gap: '20px', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,.06)', flexShrink: 0 }}>
        {[
          { icon: '🖼', action: onGallery, label: 'Gallery' },
          { icon: 'GIF', action: () => {}, label: 'GIF', isText: true },
          { icon: '🎵', action: () => {}, label: 'Music' },
        ].map((btn) => (
          <button
            key={btn.label}
            onClick={btn.action}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#72788A', fontSize: btn.isText ? '13px' : '20px', fontFamily: btn.isText ? "'Bebas Neue', sans-serif" : 'inherit', letterSpacing: btn.isText ? '1px' : 'inherit', padding: '4px', transition: 'color .12s' }}
          >
            {btn.icon}
          </button>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '11px', color: text.length > 450 ? '#F07B1D' : '#3A3F4E' }}>
            {text.length}/500
          </span>
          <button
            onClick={handlePost}
            disabled={!canPost || posting}
            style={{
              background: canPost ? '#F07B1D' : 'rgba(240,123,29,.3)',
              color: canPost ? '#050608' : 'rgba(5,6,8,.5)',
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '15px', letterSpacing: '2px',
              padding: '8px 18px', borderRadius: '20px',
              border: 'none', cursor: canPost ? 'pointer' : 'not-allowed',
              transition: 'all .14s',
              boxShadow: canPost ? '0 3px 12px rgba(240,123,29,.3)' : 'none',
            }}
          >
            {posting ? '◌' : destination === 'block' ? 'DROP IT' : 'TO COOKOUT'}
          </button>
        </div>
      </div>

      {/* DESTINATION TOGGLE */}
      <div style={{ padding: '10px 16px 16px', display: 'flex', gap: '8px', flexShrink: 0 }}>
        {[
          { id: 'block',   icon: '⬛', label: 'The Block' },
          { id: 'cookout', icon: '🍽', label: 'The Cookout' },
        ].map(d => (
          <button
            key={d.id}
            onClick={() => setDestination(d.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', borderRadius: '20px',
              background: destination === d.id ? 'rgba(240,123,29,.1)' : 'rgba(255,255,255,.04)',
              border: destination === d.id ? '1px solid rgba(240,123,29,.3)' : '1px solid rgba(255,255,255,.08)',
              color: destination === d.id ? '#F07B1D' : '#72788A',
              fontFamily: "'Instrument Sans', sans-serif",
              fontSize: '13px', cursor: 'pointer',
            }}
          >
            <span>{d.icon}</span>
            <span>{d.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── MOMENT MODE — 24hr ephemeral ───────────────────────────────────────────

function MomentMode({ onCamera, onCapture, videoRef, cameraActive, capturing, onFlip, showLensSelector, sortedLenses, currentDeviceId, getLensLabel, onSwitchLens }) {
  const [timer, setTimer] = useState(0); // 0, 3, 10
  const [countdown, setCountdown] = useState(0);
  const cycleTimer = () => setTimer(t => t === 0 ? 3 : t === 3 ? 10 : 0);

  const captureWithTimer = () => {
    if (timer === 0) { onCapture(); return; }
    let c = timer;
    setCountdown(c);
    const interval = setInterval(() => {
      c -= 1;
      setCountdown(c);
      if (c <= 0) { clearInterval(interval); onCapture(); }
    }, 1000);
  };

  const SIDE_BTN = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.85, padding: '4px' };

  return (
    <div style={{ height: '100%', position: 'relative', background: '#000', display: 'flex', flexDirection: 'column' }}>

      {/* Camera view or prompt — fills space */}
      <div style={{ flex: 1, position: 'relative', background: 'radial-gradient(ellipse at 50% 30%, rgba(50,50,70,.8), #000)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>
        {/* Always rendered so videoRef is available before camera starts */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: cameraActive ? 'block' : 'none', pointerEvents: 'none' }}
        />

        {/* Countdown overlay */}
        {countdown > 0 && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '120px', color: '#fff', textShadow: '0 0 40px rgba(0,0,0,.8)', lineHeight: 1 }}>{countdown}</div>
          </div>
        )}

        {/* Flip button — directly positioned, no wrapper div */}
        {cameraActive && (
          <button
            onClick={onFlip}
            style={{ position: 'absolute', top: '12px', right: '14px', zIndex: 20, width: '42px', height: '42px', borderRadius: '50%', background: 'rgba(0,0,0,.5)', border: '1px solid rgba(255,255,255,.2)', color: '#fff', fontSize: '18px', cursor: 'pointer', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >🔄</button>
        )}

        {/* Lens selector — directly positioned, no wrapper div */}
        {cameraActive && showLensSelector && (
          <div style={{ position: 'absolute', bottom: '108px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px', zIndex: 20 }}>
            {sortedLenses.map(device => (
              <button
                key={device.deviceId}
                onClick={() => onSwitchLens(device.deviceId)}
                style={{ padding: '5px 13px', borderRadius: '20px', background: currentDeviceId === device.deviceId ? 'rgba(255,255,255,.95)' : 'rgba(0,0,0,.5)', color: currentDeviceId === device.deviceId ? '#000' : '#fff', border: '1px solid rgba(255,255,255,.3)', fontFamily: "'Azeret Mono', monospace", fontSize: '12px', fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(8px)' }}
              >{getLensLabel(device)}</button>
            ))}
          </div>
        )}

        {/* Right sidebar — Timer + Flip when camera active */}
        {cameraActive && (
          <div style={{ position: 'absolute', right: '14px', top: '35%', display: 'flex', flexDirection: 'column', gap: '20px', zIndex: 20 }}>
            <button onClick={cycleTimer} style={SIDE_BTN}>
              <span style={{ fontSize: '22px', filter: 'drop-shadow(0 1px 4px rgba(0,0,0,.8))' }}>⏱</span>
              <span style={{ fontFamily: "'Instrument Sans', sans-serif", fontSize: '9px', color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,.8)' }}>{timer === 0 ? 'Timer' : `${timer}s`}</span>
            </button>
          </div>
        )}

        {/* Center prompt — only when camera not active */}
        {!cameraActive && (
          <>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '3px', color: 'rgba(255,255,255,.3)', zIndex: 2 }}>THE MOMENT</div>
            <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,.2)', zIndex: 2 }}>24 HOURS · THEN IT'S GONE</div>
            <button
              onClick={onCamera}
              style={{ marginTop: '14px', width: '70px', height: '70px', borderRadius: '50%', background: 'rgba(255,255,255,.12)', border: '2px solid rgba(255,255,255,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', cursor: 'pointer', zIndex: 2 }}
            >📷</button>
          </>
        )}

        {/* Left sidebar tools — zIndex above video */}
        <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: '22px', zIndex: 20 }}>
          {[
            { icon: 'Aa', label: 'Text', serif: true, onClick: () => toast.info('Text overlay coming soon ✍🏾') },
            { icon: '◫', label: 'Layout', onClick: () => toast.info('Layout tools coming soon') },
            { icon: '☺', label: 'AR', onClick: () => toast.info('AR filters coming soon ✨') },
            { icon: '🎵', label: 'Music', onClick: () => toast.info('Music integration coming soon 🎵') },
          ].map(tool => (
            <button key={tool.label} onClick={tool.onClick} style={SIDE_BTN}>
              <span style={{ fontSize: tool.serif ? '18px' : '22px', color: '#fff', fontFamily: tool.serif ? 'serif' : 'inherit', filter: 'drop-shadow(0 1px 4px rgba(0,0,0,.8))' }}>{tool.icon}</span>
              <span style={{ fontFamily: "'Instrument Sans', sans-serif", fontSize: '9px', color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,.8)' }}>{tool.label}</span>
            </button>
          ))}
        </div>

        {/* Shutter button — only when camera is active */}
        {cameraActive && (
          <button
            onClick={captureWithTimer}
            disabled={capturing || countdown > 0}
            style={{
              position: 'absolute', bottom: '28px', left: '50%', transform: 'translateX(-50%)',
              width: '72px', height: '72px', borderRadius: '50%',
              background: capturing ? 'rgba(240,123,29,.5)' : '#fff',
              border: '4px solid rgba(255,255,255,.6)',
              boxShadow: '0 0 0 3px rgba(0,0,0,.4)',
              cursor: capturing ? 'not-allowed' : 'pointer',
              zIndex: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {capturing && <div style={{ width: '28px', height: '28px', border: '2px solid #F07B1D', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />}
          </button>
        )}

        {/* Bottom emoji tray — always visible */}
        {!cameraActive && (
          <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '10px', zIndex: 3 }}>
            {['🔥', '💬', '📍', '🎵'].map(e => (
              <button key={e} style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.2)', fontSize: '20px', cursor: 'pointer', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── REEL MODE ───────────────────────────────────────────────────────────────

const REEL_FILTERS = {
  none:  'none',
  vivid: 'saturate(1.8) contrast(1.1)',
  muted: 'saturate(0.6) brightness(0.95)',
  noir:  'grayscale(1) contrast(1.2)',
  warm:  'sepia(0.3) saturate(1.4)',
  cold:  'hue-rotate(180deg) saturate(0.8)',
};
const FILTER_LABELS = { none: 'Off', vivid: 'Vivid', muted: 'Muted', noir: 'Noir', warm: 'Warm', cold: 'Cold' };
const LENGTHS = [{ label: '15s', val: 15 }, { label: '30s', val: 30 }, { label: '60s', val: 60 }, { label: '3min', val: 180 }];

function ReelMode({ onCamera, onStartRecord, onStopRecord, videoRef, cameraActive, recording, recordingSeconds, uploading, onFlip, showLensSelector, sortedLenses, currentDeviceId, getLensLabel, onSwitchLens, maxDuration, onMaxDurationChange }) {
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [showEffects, setShowEffects] = useState(false);
  const [showLength, setShowLength] = useState(false);

  return (
    <div style={{ height: '100%', position: 'relative', background: 'radial-gradient(ellipse at 50% 40%, rgba(40,20,10,.9), #000)' }}>
      {/* Always rendered so videoRef is available before camera starts */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: cameraActive ? 'block' : 'none', pointerEvents: 'none', filter: selectedFilter !== 'none' ? REEL_FILTERS[selectedFilter] : undefined }}
      />

      {/* Flip button — directly positioned, no wrapper div */}
      {cameraActive && (
        <button
          onClick={onFlip}
          style={{ position: 'absolute', top: '12px', right: '14px', zIndex: 20, width: '42px', height: '42px', borderRadius: '50%', background: 'rgba(0,0,0,.5)', border: '1px solid rgba(255,255,255,.2)', color: '#fff', fontSize: '18px', cursor: 'pointer', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >🔄</button>
      )}
      {/* Lens selector — directly positioned, no wrapper div */}
      {cameraActive && showLensSelector && (
        <div style={{ position: 'absolute', bottom: '112px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px', zIndex: 20 }}>
          {sortedLenses.map(device => (
            <button
              key={device.deviceId}
              onClick={() => onSwitchLens(device.deviceId)}
              style={{ padding: '5px 13px', borderRadius: '20px', background: currentDeviceId === device.deviceId ? 'rgba(255,255,255,.95)' : 'rgba(0,0,0,.5)', color: currentDeviceId === device.deviceId ? '#000' : '#fff', border: '1px solid rgba(255,255,255,.3)', fontFamily: "'Azeret Mono', monospace", fontSize: '12px', fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(8px)' }}
            >{getLensLabel(device)}</button>
          ))}
        </div>
      )}
      {!cameraActive && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '4px', color: 'rgba(255,255,255,.2)' }}>THE REEL</div>
          <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: 'rgba(255,255,255,.15)', marginTop: '6px', textTransform: 'uppercase' }}>Short. Sharp. BLVX energy.</div>
        </div>
      )}

      {/* Recording indicator */}
      {recording && (
        <div style={{ position: 'absolute', top: '16px', left: '16px', display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(220,38,38,.9)', padding: '4px 10px', borderRadius: '4px', zIndex: 5 }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff', animation: 'pip 1.2s ease-in-out infinite' }} />
          <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '10px', letterSpacing: '2px', color: '#fff' }}>{recordingSeconds}s</span>
        </div>
      )}

      {/* Uploading indicator */}
      {uploading && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', zIndex: 6 }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid rgba(240,123,29,.3)', borderTopColor: '#F07B1D', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '10px', letterSpacing: '2px', color: '#EAE0CC' }}>UPLOADING REEL...</span>
        </div>
      )}

      <div style={{ position: 'absolute', left: '14px', top: '30%', display: 'flex', flexDirection: 'column', gap: '22px', zIndex: 2 }}>
        {[
          { icon: '🎵', label: 'Audio', onClick: () => toast.info('Music integration coming soon 🎵') },
          { icon: '✦', label: 'Effects', onClick: () => setShowEffects(v => !v) },
          { icon: '⏱', label: 'Length', onClick: () => setShowLength(v => !v) },
          { icon: '🎭', label: 'Green Screen', onClick: () => toast.info('Green Screen coming soon 🎬') },
        ].map(tool => (
          <button
            key={tool.label}
            onClick={tool.onClick}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.85, padding: '4px' }}
          >
            <span style={{ fontSize: '22px', filter: 'drop-shadow(0 1px 4px rgba(0,0,0,.8))' }}>{tool.icon}</span>
            <span style={{ fontFamily: "'Instrument Sans', sans-serif", fontSize: '10px', color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,.8)', whiteSpace: 'nowrap' }}>{tool.label}</span>
          </button>
        ))}
      </div>

      {/* Effects bottom sheet */}
      {showEffects && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(5,6,8,.96)', borderTop: '1px solid rgba(255,255,255,.08)', padding: '16px', zIndex: 20 }}>
          <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '3px', textTransform: 'uppercase', color: '#72788A', marginBottom: '12px' }}>Video Effects</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {Object.keys(REEL_FILTERS).map(key => (
              <button
                key={key}
                onClick={() => { setSelectedFilter(key); setShowEffects(false); }}
                style={{ padding: '8px 16px', borderRadius: '20px', background: selectedFilter === key ? '#F07B1D' : 'rgba(255,255,255,.08)', color: selectedFilter === key ? '#050608' : '#EAE0CC', border: selectedFilter === key ? 'none' : '1px solid rgba(255,255,255,.1)', fontFamily: "'Bebas Neue', sans-serif", fontSize: '14px', letterSpacing: '1px', cursor: 'pointer' }}
              >{FILTER_LABELS[key]}</button>
            ))}
          </div>
        </div>
      )}

      {/* Length bottom sheet */}
      {showLength && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(5,6,8,.96)', borderTop: '1px solid rgba(255,255,255,.08)', padding: '16px', zIndex: 20 }}>
          <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '3px', textTransform: 'uppercase', color: '#72788A', marginBottom: '12px' }}>Max Length</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {LENGTHS.map(({ label, val }) => (
              <button
                key={val}
                onClick={() => { onMaxDurationChange(val); setShowLength(false); }}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', background: maxDuration === val ? '#F07B1D' : 'rgba(255,255,255,.08)', color: maxDuration === val ? '#050608' : '#EAE0CC', border: maxDuration === val ? 'none' : '1px solid rgba(255,255,255,.1)', fontFamily: "'Bebas Neue', sans-serif", fontSize: '16px', letterSpacing: '1px', cursor: 'pointer' }}
              >{label}</button>
            ))}
          </div>
        </div>
      )}

      {/* Record / stop button */}
      <div style={{ position: 'absolute', bottom: '30px', left: '50%', transform: 'translateX(-50%)', zIndex: 4 }}>
        {!cameraActive ? (
          <button
            onClick={onCamera}
            style={{ width: '74px', height: '74px', borderRadius: '50%', background: 'rgba(240,123,29,.9)', border: '3px solid rgba(255,255,255,.8)', boxShadow: '0 0 24px rgba(240,123,29,.5)', cursor: 'pointer', fontSize: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >🎬</button>
        ) : recording ? (
          <button
            onClick={onStopRecord}
            style={{ width: '74px', height: '74px', borderRadius: '50%', background: '#DC2626', border: '4px solid rgba(255,255,255,.8)', boxShadow: '0 0 24px rgba(220,38,38,.6)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div style={{ width: '24px', height: '24px', background: '#fff', borderRadius: '3px' }} />
          </button>
        ) : (
          <button
            onClick={onStartRecord}
            style={{ width: '74px', height: '74px', borderRadius: '50%', background: '#fff', border: '4px solid rgba(255,255,255,.6)', boxShadow: '0 0 0 3px rgba(0,0,0,.4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#DC2626' }} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── LIVE MODE — The Stoop Live ──────────────────────────────────────────────

function LiveMode() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [sharing, setSharing] = useState('everyone');
  const [cameraReady, setCameraReady] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [goingLive, setGoingLive] = useState(false);
  const [stoopId, setStoopId] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const roomRef = useRef(null);
  const isLiveRef = useRef(false);

  // Show camera preview before going live
  useEffect(() => {
    const startPreview = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: true,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setCameraReady(true);
      } catch (e) {
        console.warn('[LiveMode] Camera preview unavailable:', e);
      }
    };
    startPreview();
    return () => {
      // Only stop preview stream — if live, LiveKit manages camera
      if (!isLiveRef.current) {
        streamRef.current?.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const goLive = async () => {
    if (goingLive) return;
    setGoingLive(true);
    try {
      // 1. Create stoop session
      const createRes = await api.post('/stoop/create', {
        title: title || 'Live on The Stoop',
      }, { withCredentials: true });
      const sId = createRes.data?.id || createRes.data?.stoop_id;
      setStoopId(sId);

      // 2. Get LiveKit token — backend returns both token and url
      const tokenRes = await api.get(`/stoop/${sId}/livekit-token`, { withCredentials: true });
      const token = tokenRes.data?.token;
      const livekitUrl = tokenRes.data?.url;
      if (!token) throw new Error('No LiveKit token returned');
      if (!livekitUrl) throw new Error('No LiveKit URL returned from server');

      // 3. Stop preview stream so LiveKit can acquire camera cleanly
      streamRef.current?.getTracks().forEach(t => t.stop());

      // 4. Connect LiveKit room
      const room = new Room();
      roomRef.current = room;
      await room.connect(livekitUrl, token);

      // 5. Enable camera + mic
      await room.localParticipant.setCameraEnabled(true);
      await room.localParticipant.setMicrophoneEnabled(true);

      // 6. Attach local video track to preview element
      const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      if (camPub?.videoTrack && videoRef.current) {
        camPub.videoTrack.attach(videoRef.current);
      }

      isLiveRef.current = true;
      setIsLive(true);
      localStorage.setItem('blvx-lastUsedFormat', 'live');

      // Post live card to Block feed (best-effort)
      try {
        await api.post('/posts', {
          post_type: 'live',
          content: title || 'Live on The Stoop',
          destination: 'block',
        }, { withCredentials: true });
      } catch {}
    } catch (e) {
      console.error('[LiveMode] Go live failed:', e);
      toast.error('Failed to go live — check permissions and try again');
    } finally {
      setGoingLive(false);
    }
  };

  const endLive = async () => {
    try {
      roomRef.current?.disconnect();
      if (stoopId) {
        await api.post(`/stoop/${stoopId}/end`, {}, { withCredentials: true });
      }
    } catch {}
    navigate('/home');
  };

  // ── LIVE VIEW — rendered in-place, no navigation ──
  if (isLive) {
    return (
      <div style={{ position: 'absolute', inset: 0, background: '#000' }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
        />
        {/* LIVE badge */}
        <div style={{ position: 'absolute', top: '16px', left: '16px', display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(220,38,38,.9)', padding: '4px 10px', borderRadius: '4px', zIndex: 10 }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff', animation: 'pip 1.4s ease-in-out infinite' }} />
          <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '10px', letterSpacing: '2px', color: '#fff' }}>LIVE</span>
        </div>
        {/* END LIVE button */}
        <button
          onClick={endLive}
          style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 10, background: 'rgba(220,38,38,.9)', color: '#fff', fontFamily: "'Bebas Neue', sans-serif", fontSize: '14px', letterSpacing: '1.5px', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer' }}
        >⏹ END LIVE</button>
        {/* Title overlay */}
        {title && (
          <div style={{ position: 'absolute', bottom: '40px', left: '16px', right: '16px', zIndex: 10 }}>
            <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', letterSpacing: '2px', color: '#EAE0CC', textShadow: '0 2px 8px rgba(0,0,0,.8)', margin: 0 }}>{title}</p>
          </div>
        )}
      </div>
    );
  }

  // ── PRE-LIVE VIEW — camera preview + controls ──
  return (
    <div style={{ position: 'relative', height: '100%', background: '#000' }}>
      {/* Camera preview — host sees themselves before going live */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
      />

      {/* Dark gradient overlay */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,.85) 0%, rgba(0,0,0,.2) 50%, transparent 100%)' }} />

      {/* No camera fallback */}
      {!cameraReady && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', zIndex: 1 }}>
          <div style={{ fontSize: '48px' }}>🎙</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '3px', color: 'rgba(255,255,255,.3)' }}>THE STOOP IS OPEN</div>
        </div>
      )}

      {/* Bottom controls */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px', zIndex: 2 }}>
        <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,.4)', marginBottom: '8px' }}>Share with</div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          {[
            { id: 'everyone', icon: '🌍', label: 'Everyone' },
            { id: 'cookout',  icon: '🍽', label: 'The Cookout' },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setSharing(opt.id)}
              style={{
                flex: 1, padding: '10px',
                background: sharing === opt.id ? 'rgba(240,123,29,.2)' : 'rgba(0,0,0,.4)',
                border: sharing === opt.id ? '1px solid rgba(240,123,29,.5)' : '1px solid rgba(255,255,255,.15)',
                borderRadius: '10px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                color: sharing === opt.id ? '#F07B1D' : 'rgba(255,255,255,.6)',
                fontFamily: "'Instrument Sans', sans-serif", fontSize: '14px',
                backdropFilter: 'blur(8px)',
              }}
            >
              <span>{opt.icon}</span><span>{opt.label}</span>
            </button>
          ))}
        </div>

        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="What are we talking about? (optional)"
          style={{
            width: '100%', background: 'rgba(0,0,0,.5)',
            border: '1px solid rgba(255,255,255,.15)',
            borderRadius: '10px', padding: '12px 14px',
            fontFamily: "'Instrument Sans', sans-serif",
            fontSize: '15px', color: '#EAE0CC',
            outline: 'none', marginBottom: '12px', boxSizing: 'border-box',
            backdropFilter: 'blur(8px)',
          }}
        />

        <button
          onClick={goLive}
          disabled={goingLive}
          style={{
            width: '100%', background: goingLive ? 'rgba(220,38,38,.5)' : '#DC2626',
            color: '#fff', fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '20px', letterSpacing: '3px',
            border: 'none', borderRadius: '12px', padding: '16px',
            cursor: goingLive ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            boxShadow: '0 4px 24px rgba(220,38,38,.5)',
          }}
        >
          {goingLive ? (
            <div style={{ width: '18px', height: '18px', border: '2px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          ) : (
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
          )}
          {goingLive ? 'GOING LIVE...' : 'GO LIVE ON THE STOOP'}
        </button>
      </div>
    </div>
  );
}

// ─── ROTATION MODE — multi-slide portrait story ───────────────────────────────

function RotationMode() {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', background: '#050608' }}>
      <div style={{ fontSize: '48px' }}>🎠</div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '4px', color: 'rgba(255,255,255,.3)', textAlign: 'center' }}>THE ROTATION</div>
      <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: 'rgba(255,255,255,.15)', textTransform: 'uppercase', textAlign: 'center' }}>Multi-slide portrait story</div>
      <div style={{ marginTop: '8px', fontFamily: "'Instrument Sans', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,.25)', textAlign: 'center', padding: '0 32px' }}>
        Slide builder coming soon
      </div>
    </div>
  );
}

// ─── BYLINE MODE — long-form press story ─────────────────────────────────────

function BylineMode({ user }) {
  const navigate = useNavigate();
  const [headline, setHeadline] = useState('');
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);

  const isPress = user?.press_tier === 'verified_press' || user?.press_tier === 'campus_reporter';

  if (!isPress) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', background: '#050608' }}>
        <div style={{ fontSize: '40px' }}>🔒</div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', letterSpacing: '3px', color: 'rgba(255,255,255,.3)', textAlign: 'center' }}>Press Access Only</div>
        <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: 'rgba(255,255,255,.15)', textAlign: 'center', padding: '0 32px' }}>
          BYLINE IS FOR VERIFIED PRESS &amp; CAMPUS REPORTERS
        </div>
      </div>
    );
  }

  const handlePublish = async () => {
    if (!headline.trim()) return;
    setPosting(true);
    try {
      await api.post('/posts', {
        post_type: 'byline',
        content: headline.trim(),
        body: body.trim(),
      }, { withCredentials: true });
      localStorage.setItem('blvx-lastUsedFormat', 'byline');
      toast.success('Byline published!');
      navigate('/home');
    } catch {
      toast.error('Failed to publish byline');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#050608' }}>
      <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', overflow: 'auto' }}>
        <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', color: '#3A3F4E', textTransform: 'uppercase' }}>Headline</div>
        <input
          value={headline}
          onChange={e => setHeadline(e.target.value)}
          placeholder="Write your headline..."
          style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: '8px', padding: '12px 14px', color: '#EAE0CC', fontFamily: "'Playfair Display', serif", fontSize: '18px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
        />
        <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', color: '#3A3F4E', textTransform: 'uppercase', marginTop: '4px' }}>Story</div>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="Tell the story..."
          rows={10}
          style={{ flex: 1, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: '8px', padding: '12px 14px', color: '#EAE0CC', fontFamily: "'Instrument Sans', sans-serif", fontSize: '15px', lineHeight: 1.6, outline: 'none', resize: 'none', width: '100%', boxSizing: 'border-box' }}
        />
      </div>
      <div style={{ padding: '12px 16px 16px', borderTop: '1px solid rgba(255,255,255,.06)', flexShrink: 0 }}>
        <button
          onClick={handlePublish}
          disabled={!headline.trim() || posting}
          style={{ width: '100%', background: headline.trim() ? '#F07B1D' : 'rgba(240,123,29,.3)', color: headline.trim() ? '#050608' : 'rgba(5,6,8,.5)', fontFamily: "'Bebas Neue', sans-serif", fontSize: '16px', letterSpacing: '2px', padding: '14px', borderRadius: '10px', border: 'none', cursor: headline.trim() ? 'pointer' : 'not-allowed' }}
        >
          {posting ? '◌' : 'PUBLISH BYLINE'}
        </button>
      </div>
    </div>
  );
}

// ─── MODE SWITCHER ────────────────────────────────────────────────────────────

const MODES = [
  { id: 'drop',     label: 'DROP' },
  { id: 'moment',   label: 'MOMENT' },
  { id: 'reel',     label: 'REEL' },
  { id: 'rotation', label: 'ROTATION' },
  { id: 'live',     label: 'LIVE' },
];

function ModeSwitcher({ current, onChange }) {
  return (
    <div style={{
      height: '64px', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
      background: 'rgba(5,6,8,.95)',
      borderTop: '1px solid rgba(255,255,255,.06)',
      backdropFilter: 'blur(12px)',
    }}>
      {MODES.map(m => (
        <button
          key={m.id}
          onClick={() => onChange(m.id)}
          style={{
            background: 'none', border: 'none',
            padding: '8px 12px', cursor: 'pointer',
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '14px', letterSpacing: '2px',
            color: current === m.id ? '#F07B1D' : '#3A3F4E',
            borderBottom: current === m.id ? '2px solid #F07B1D' : '2px solid transparent',
            transition: 'all .14s', whiteSpace: 'nowrap',
          }}
        >
          {m.id === 'live' && current === 'live' && (
            <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#DC2626', marginRight: '5px', verticalAlign: 'middle', animation: 'pip 1.4s ease-in-out infinite' }} />
          )}
          {m.label}
        </button>
      ))}
    </div>
  );
}

// ─── MAIN DROP PAGE ───────────────────────────────────────────────────────────

const DropPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [mode, setMode] = useState(() => {
    const param = searchParams.get('mode');
    const valid = ['drop', 'moment', 'reel', 'rotation', 'live', 'byline'];
    return valid.includes(param) ? param : 'drop';
  });
  const [cameraActive, setCameraActive] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [reelUploading, setReelUploading] = useState(false);
  const [maxDuration, setMaxDuration] = useState(60); // seconds

  // Camera device state — flip + lens switching
  const [devices, setDevices] = useState([]);
  const [currentDeviceId, setCurrentDeviceId] = useState('');
  const [facingMode, setFacingMode] = useState('environment');

  // Preview state — shown after capture/record, before posting
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [capturedPreviewUrl, setCapturedPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [capturedType, setCapturedType] = useState(null); // 'moment' | 'reel'
  const [posting, setPosting] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // Retake — clear preview and reopen camera
  const handleRetake = () => {
    if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
    setCapturedBlob(null);
    setCapturedPreviewUrl(null);
    setShowPreview(false);
    setCapturedType(null);
    setCameraActive(false);
  };

  // Post captured moment or reel after preview confirm
  const handlePostCaptured = async () => {
    if (!capturedBlob || posting) return;
    setPosting(true);
    try {
      const filename = capturedType === 'reel' ? 'reel.webm' : 'moment.jpg';
      const formData = new FormData();
      formData.append('file', capturedBlob, filename);
      const uploadRes = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      const mediaUrl = uploadRes.data?.url || uploadRes.data?.secure_url;
      if (!mediaUrl) throw new Error('Upload failed');

      if (capturedType === 'reel') {
        await api.post('/posts', { post_type: 'reel', media_url: mediaUrl, media_type: 'video' });
        localStorage.setItem('blvx-lastUsedFormat', 'reel');
        toast.success('Reel dropped 🎬');
      } else {
        await api.post('/posts', {
          post_type: 'moment',
          media_url: mediaUrl,
          media_type: 'image',
          expires_at: new Date(Date.now() + 86400000).toISOString(),
        });
        localStorage.setItem('blvx-lastUsedFormat', 'moment');
        toast.success('Moment dropped! Gone in 24h ⏳');
      }
      navigate('/home');
    } catch {
      toast.error(`Failed to post ${capturedType}`);
    } finally {
      setPosting(false);
    }
  };

  const captureMoment = () => {
    if (!videoRef.current || capturing) return;
    setCapturing(true);
    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1080;
      canvas.height = video.videoHeight || 1920;
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        // Stop camera stream
        streamRef.current?.getTracks().forEach(t => t.stop());
        setCameraActive(false);
        // Show preview before posting
        const url = URL.createObjectURL(blob);
        setCapturedBlob(blob);
        setCapturedPreviewUrl(url);
        setCapturedType('moment');
        setShowPreview(true);
        setCapturing(false);
      }, 'image/jpeg', 0.9);
    } catch {
      toast.error('Camera capture failed');
      setCapturing(false);
    }
  };

  const startReelRecording = () => {
    if (!streamRef.current || recording) return;
    recordingChunksRef.current = [];
    const mr = new MediaRecorder(streamRef.current, { mimeType: 'video/webm;codecs=vp8,opus' });
    mr.ondataavailable = (e) => { if (e.data.size > 0) recordingChunksRef.current.push(e.data); };
    mr.onstop = () => {
      clearInterval(recordingTimerRef.current);
      setRecordingSeconds(0);
      // Stop camera, show preview
      streamRef.current?.getTracks().forEach(t => t.stop());
      setCameraActive(false);
      const blob = new Blob(recordingChunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      setCapturedBlob(blob);
      setCapturedPreviewUrl(url);
      setCapturedType('reel');
      setShowPreview(true);
    };
    mr.start();
    mediaRecorderRef.current = mr;
    setRecording(true);
    setRecordingSeconds(0);
    recordingTimerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
    // Auto-stop at maxDuration
    setTimeout(() => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
        setRecording(false);
      }
    }, maxDuration * 1000);
  };

  const stopReelRecording = () => {
    if (!mediaRecorderRef.current || !recording) return;
    setRecording(false);
    mediaRecorderRef.current.stop();
  };

  const handleClose = () => {
    clearInterval(recordingTimerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
    navigate(-1);
  };

  const handleModeChange = (newMode) => {
    // Stop camera when switching away from camera-active mode
    if (mode !== newMode && cameraActive) {
      streamRef.current?.getTracks().forEach(t => t.stop());
      setCameraActive(false);
    }
    setMode(newMode);
  };

  // startStream — stop old stream, start new one with specific device
  const startStream = async (deviceId, withAudio) => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: deviceId
          ? { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
          : { facingMode: 'environment' },
        audio: !!withAudio,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraActive(true);
    } catch {
      openGallery();
    }
  };

  const openCamera = async () => {
    const withAudio = mode === 'reel' || mode === 'live';
    try {
      // Call getUserMedia once first to unlock device labels on iOS
      const init = await navigator.mediaDevices.getUserMedia({ video: true, audio: withAudio });
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter(d => d.kind === 'videoinput');
      setDevices(cams);
      // Prefer back camera for MOMENT/REEL
      const back = cams.find(d => /back|rear/i.test(d.label));
      const front = cams.find(d => /front|facetime/i.test(d.label));
      const target = back || front || cams[0];
      const targetId = target?.deviceId || '';
      setCurrentDeviceId(targetId);
      setFacingMode(/front|facetime/i.test(target?.label || '') ? 'user' : 'environment');
      // Stop the unlock stream and start the real one
      init.getTracks().forEach(t => t.stop());
      if (targetId) {
        await startStream(targetId, withAudio);
      } else {
        // Fallback if no labeled device
        const fallback = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: withAudio,
        });
        streamRef.current = fallback;
        if (videoRef.current) videoRef.current.srcObject = fallback;
        setCameraActive(true);
      }
    } catch {
      openGallery();
    }
  };

  // flipCamera — toggle front/back
  const flipCamera = async () => {
    if (!cameraActive || devices.length < 2) return;
    const newFacing = facingMode === 'user' ? 'environment' : 'user';
    const target = devices.find(d => {
      const label = d.label.toLowerCase();
      return newFacing === 'user'
        ? /front|facetime/.test(label)
        : /back|rear/.test(label);
    }) || devices.find(d => d.deviceId !== currentDeviceId);
    if (target) {
      setCurrentDeviceId(target.deviceId);
      setFacingMode(newFacing);
      await startStream(target.deviceId, mode === 'reel');
    }
  };

  const openGallery = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = mode === 'reel' ? 'video/*' : 'image/*,video/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setSelectedMedia(file);
      setPreviewUrl(URL.createObjectURL(file));
    };
    input.click();
  };

  // Lens selector helpers — only show for back cameras with multiple lenses
  const backCameras = devices.filter(d => /back|rear|environment/i.test(d.label));
  const getLensLabel = (device) => {
    const label = device.label.toLowerCase();
    if (/ultra|wide/i.test(label)) return '0.5×';
    if (/telephoto|tele/i.test(label)) return '3×';
    return '1×';
  };
  const sortedLenses = [...backCameras].sort((a, b) => {
    const order = ['ultra', 'wide', 'telephoto', 'tele'];
    const ai = order.findIndex(k => a.label.toLowerCase().includes(k));
    const bi = order.findIndex(k => b.label.toLowerCase().includes(k));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  const showLensSelector = facingMode === 'environment' && sortedLenses.length > 1;

  const cameraControlProps = {
    onFlip: flipCamera,
    showLensSelector,
    sortedLenses,
    currentDeviceId,
    getLensLabel,
    onSwitchLens: (deviceId) => { setCurrentDeviceId(deviceId); startStream(deviceId, mode === 'reel'); },
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#050608', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>

      {/* TOP BAR */}
      <div style={{ height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', flexShrink: 0 }}>
        <button
          onClick={handleClose}
          style={{ background: 'none', border: 'none', color: '#EAE0CC', fontSize: '22px', cursor: 'pointer', padding: '8px', margin: '-8px' }}
        >✕</button>

        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', letterSpacing: '3px', color: '#EAE0CC' }}>
          {MODE_LABELS[mode] || ''}
        </div>

        {mode === 'live' ? (
          <button style={{ background: '#18B574', color: '#fff', fontFamily: "'Bebas Neue', sans-serif", fontSize: '14px', letterSpacing: '1.5px', padding: '7px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer' }}>
            GO LIVE
          </button>
        ) : (
          <div style={{ width: '60px' }} />
        )}
      </div>

      {/* MAIN CONTENT */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {/* Preview screen — shown after capture/record before posting */}
        {showPreview && capturedPreviewUrl ? (
          <div style={{ position: 'absolute', inset: 0, background: '#000', display: 'flex', flexDirection: 'column' }}>
            {/* Preview media */}
            <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
              {capturedType === 'reel' ? (
                <video
                  src={capturedPreviewUrl}
                  autoPlay
                  loop
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <img
                  src={capturedPreviewUrl}
                  alt="Preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
              {/* Label */}
              <div style={{
                position: 'absolute', top: '16px', left: '16px',
                fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '3px',
                color: '#EAE0CC', background: 'rgba(0,0,0,0.5)', padding: '4px 10px', borderRadius: '4px',
                textTransform: 'uppercase',
              }}>
                {capturedType === 'reel' ? 'Preview — Tap POST to drop' : '24h Moment — Tap POST to drop'}
              </div>
            </div>

            {/* Retake / Post actions */}
            <div style={{
              padding: '16px', display: 'flex', gap: '12px',
              background: 'rgba(5,6,8,0.95)', flexShrink: 0,
            }}>
              <button
                onClick={handleRetake}
                style={{
                  flex: 1, padding: '14px', borderRadius: '8px',
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                  color: '#EAE0CC', fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: '17px', letterSpacing: '2px', cursor: 'pointer',
                }}
              >← Retake</button>
              <button
                onClick={handlePostCaptured}
                disabled={posting}
                style={{
                  flex: 2, padding: '14px', borderRadius: '8px',
                  background: posting ? 'rgba(240,123,29,0.5)' : '#F07B1D',
                  border: 'none',
                  color: '#050608', fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: '17px', letterSpacing: '2px', cursor: posting ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                }}
              >
                {posting && <div style={{ width: '16px', height: '16px', border: '2px solid rgba(5,6,8,0.4)', borderTopColor: '#050608', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />}
                {posting ? 'Posting...' : 'POST →'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {mode === 'drop'     && <DropMode onGallery={openGallery} selectedMedia={selectedMedia} previewUrl={previewUrl} user={user} />}
            {mode === 'moment'   && <MomentMode onCamera={openCamera} onCapture={captureMoment} videoRef={videoRef} cameraActive={cameraActive} capturing={capturing} {...cameraControlProps} />}
            {mode === 'reel'     && <ReelMode onCamera={openCamera} onStartRecord={startReelRecording} onStopRecord={stopReelRecording} videoRef={videoRef} cameraActive={cameraActive} recording={recording} recordingSeconds={recordingSeconds} uploading={reelUploading} maxDuration={maxDuration} onMaxDurationChange={setMaxDuration} {...cameraControlProps} />}
            {mode === 'rotation' && <RotationMode />}
            {mode === 'live'     && <LiveMode />}
            {mode === 'byline'   && <BylineMode user={user} />}
          </>
        )}
      </div>

      {/* BOTTOM MODE SWITCHER — hidden during preview */}
      {!showPreview && <ModeSwitcher current={mode} onChange={handleModeChange} />}
    </div>
  );
};

export default DropPage;
